import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { App_FormBloc, App_FormCreatorJson, App_FormSketchField, App_FormSketchFormDynamique, App_FormStep, App_TrueField } from 'src/app/utils/interfaces';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ActionSheetController, AlertController, ModalController } from '@ionic/angular';
import { CModalPlanComponent } from '../../c-modal/c-modal-plan/c-modal-plan.component';
import { Subject, combineLatest, debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';
import { SafeLiftService } from 'src/app/services/app/safelift.service';
import { Type_App_Validator } from 'src/app/utils/types';

@Component({
  selector: 'app-c-sketch',
  templateUrl: './c-sketch.component.html',
  styleUrls: ['./c-sketch.component.scss'],
})
export class CSketchComponent implements OnInit, OnDestroy {
  @Input({ required: true }) field!: App_FormSketchField;
  @Input({ required: true }) cFormGroup!: FormGroup;
  @Input({ required: true }) blocName!: string;
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) contextFromParentPage!: 'preparation' | 'edition';
  @Input({ required: true }) modeShow!: boolean;
  @Input({ required: true }) observeSourcesArrFromParent!: { formControl: FormControl; validator: Type_App_Validator; }[];
  @Input({ required: true }) display!: App_FormCreatorJson;
  @Input({ required: true }) step!: App_FormStep;
  @Input({ required: true }) bloc!: App_FormBloc;
  @Input() etatId?: number;

  @Output() triggerAlert = new EventEmitter<{
    blocName: string;
    fieldName: string;
    fieldFormControl: AbstractControl<any, any>;
    field: App_TrueField;
  }>();
  @Output() formArraySubGroupEditor = new EventEmitter<{ formSketchFormDynamique: App_FormSketchFormDynamique, formGroup: FormGroup, blocName: string, step: App_FormStep, bloc: App_FormBloc }>();
  @HostListener('window:resize', ['$event']) onResize(event: any) {
    this.resizedWindowTrigger$.next(true);
  }

  imageUploadFormControl: FormControl | undefined;
  drawingFormControl: FormControl | undefined;
  markersFormControl: FormControl | undefined;
  formDynamiqueFormArray: FormArray | undefined;

  /** Fond affiché hors modal : upload, image fixe (string) ou image_default */
  displayBackgroundUrl: string | null = null;

  pingUpdateMarqueurs$ = new Subject();
  resizedWindowTrigger$ = new Subject()

  marqueurs = [];
  marqueurSize = environment.marqueurPlanSize;

  destroyComponent$ = new Subject();

  constructor(
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private safeLiftService: SafeLiftService,
  ) { }

  ngOnInit() {
    this.imageUploadFormControl = this.field.fields.image && typeof this.field.fields.image !== 'string' && typeof this.field.fields.image.name === 'string' && this.cFormGroup.controls[this.field.fields.image.name] ? this.cFormGroup.controls[this.field.fields.image.name] as FormControl : undefined;
    this.drawingFormControl = this.field.fields.drawing && typeof this.field.fields.drawing !== 'string' && typeof this.field.fields.drawing.name === 'string' && this.cFormGroup.controls[this.field.fields.drawing.name] ? this.cFormGroup.controls[this.field.fields.drawing.name] as FormControl : undefined;
    this.markersFormControl = this.field.fields.markers && typeof this.field.fields.markers !== 'string' && typeof this.field.fields.markers.name === 'string' && this.cFormGroup.controls[this.field.fields.markers.name] ? this.cFormGroup.controls[this.field.fields.markers.name] as FormControl : undefined;
    this.formDynamiqueFormArray = this.field.fields.prototypeOnAddMarker && typeof this.field.fields.prototypeOnAddMarker.name === 'string' && this.cFormGroup.controls[this.field.fields.prototypeOnAddMarker.name] ? this.cFormGroup.controls[this.field.fields.prototypeOnAddMarker.name] as FormArray : undefined;
    this.pingUpdateMarqueurs$.next(true);
    this.refreshDisplayBackgroundUrl();

    if (this.imageUploadFormControl) {
      this.imageUploadFormControl.valueChanges.pipe(
        takeUntil(this.destroyComponent$),
        startWith(this.imageUploadFormControl.value),
      ).subscribe(() => this.refreshDisplayBackgroundUrl());
    }

    if (this.markersFormControl) {
      this.markersFormControl.valueChanges.pipe(
        takeUntil(this.destroyComponent$),
        startWith(this.markersFormControl.value)
      ).subscribe(e => {
        this.marqueurs = e || [];
        this.pingUpdateMarqueurs$.next(e);
      })
    }
    
    const formDynamique = this.field.fields.prototypeOnAddMarker;
    if (this.markersFormControl && this.formDynamiqueFormArray && formDynamique) {
      this.formDynamiqueFormArray.valueChanges.pipe(
        startWith(this.formDynamiqueFormArray.value),
        takeUntil(this.destroyComponent$)
      ).subscribe(_ => {
        this.resizedWindowTrigger$.next(true);
      })

      const formDynamiqueLengthValEvolution$ = this.formDynamiqueFormArray.valueChanges.pipe(
        startWith(this.formDynamiqueFormArray.value),
        map(val => Array.isArray(val) ? val.length : 0),
        distinctUntilChanged()
      );
      const markersLengthValEvolution$ = this.markersFormControl.valueChanges.pipe(
        map(val => Array.isArray(val) ? val.length : 0),
        distinctUntilChanged()
      );

      if (!this.modeShow) {
        combineLatest([formDynamiqueLengthValEvolution$, markersLengthValEvolution$]).pipe(
          debounceTime(200),
          takeUntil(this.destroyComponent$)
        ).subscribe(([formDynamiqueCurrentLength, markersCurrentLength]) => {
          if (formDynamiqueCurrentLength < markersCurrentLength) {
            const amountGroupsToGenerate = markersCurrentLength - formDynamiqueCurrentLength;
            if (this.formDynamiqueFormArray) {
              for (let index = 1; index <= amountGroupsToGenerate; index++) {
                const formGroup = this.generateFormDynamique(formDynamique);
                this.formDynamiqueFormArray.push(formGroup);
                this.resizedWindowTrigger$.next(true);
              }
            }
          } else if (formDynamiqueCurrentLength > markersCurrentLength) {
            const amountGroupsToDelete = formDynamiqueCurrentLength - markersCurrentLength;
            if (this.formDynamiqueFormArray) {
              for (let index = 1; index <= amountGroupsToDelete; index++) {
                this.formDynamiqueFormArray.removeAt(this.formDynamiqueFormArray.length - 1);
                this.resizedWindowTrigger$.next(true);
              }
            }
          }
        })
      }
    }
  }

  emitMarkerClicked(markerIndex: number) {
    if (this.formDynamiqueFormArray) {
      const formGroup = this.formDynamiqueFormArray.controls[markerIndex];
      const formSketchFormDynamique = this.field.fields.prototypeOnAddMarker;
      if (formSketchFormDynamique) {
        if (formGroup instanceof FormGroup) {
          this.formArraySubGroupEditor.emit({ 
            formSketchFormDynamique,
            formGroup,
            blocName: this.blocName,
            step: this.step,
            bloc: this.bloc,
          });
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroyComponent$.next(true);
  }

  openModalDrawingMarkers() {
    console.log('openModalDrawingMarkers');
    console.log( this.isReadonly());
    console.log(this.etatId);
    if (this.modeShow || this.isReadonly()) {
      return;
    }
    if (!this.modeShow) {
      const canDraw = this.drawingFormControl;
      const canSetMarkers = this.markersFormControl;
      // Même source que l'aperçu formulaire (URL fixe / upload / image_default)
      const imageDataBase64 = this.displayBackgroundUrl ?? this.getBackgroundImageUrl() ?? undefined;
      if (canDraw || canSetMarkers) {
        const marqueursDatas = this.field?.fields?.markers?.datas;
        const limitMarkers = this.field?.fields?.markers?.limitMarkers;
        const labelsMarqueurs = marqueursDatas !== null && marqueursDatas !== undefined
        ? Object.entries(marqueursDatas)
        : undefined;
        const limitNbMarqueur = limitMarkers || 100;
        this.modalController.create({
          component: CModalPlanComponent,
          backdropDismiss: false,
          cssClass: `gl-modal-90`,
          componentProps: {
            title: typeof this.field.label === 'string' && this.field.label. length > 0 ? this.field.label : '',
            imageDataBase64,
            initialDrawing: this.drawingFormControl?.value,
            initialMarkers: this.markersFormControl?.value,
            field: this.field,
            canDraw,
            canSetMarkers,
            limitNbMarqueur,
            labelsMarqueurs,
          }
        }).then(modal => {
          modal.onDidDismiss().then(x => {
            if (x.data !== null) {
              if (x.data['marqueurs'] && this.markersFormControl) {
                this.markersFormControl.setValue(x.data['marqueurs'])
                this.markersFormControl.markAsDirty();
                this.markersFormControl.updateValueAndValidity();
              }
              if (typeof x.data['dessin'] === 'string' && this.drawingFormControl) {
                this.drawingFormControl.setValue(x.data['dessin'])
                this.drawingFormControl.markAsDirty();
                this.drawingFormControl.updateValueAndValidity();
              }
            }
            requestAnimationFrame(() => {
              this.resizedWindowTrigger$.next(true)
            });
          })
          modal.present();
        })
      }
    }
  }

  onImageButtonClick() {
    console.log('onImageButtonClick');
    console.log( this.isReadonly());
    console.log(this.etatId);
    if (this.modeShow || this.isReadonly()) {
      return;
    }
    if (!this.modeShow) {
      if (this.imageUploadFormControl) {
        const imgFC = this.imageUploadFormControl;
        if (!imgFC.value) {
          this.uploadImage(imgFC)
        } else {
          this.imageOptions(imgFC)
        }
      }
    }
  }

  imageOptions(imgFC: FormControl<any>) {
    this.actionSheetController.create({
      header: 'Action',
      buttons: [
        {
        text: `Sélectionner une autre image`,
        handler: () => {
          this.uploadImage(imgFC)
        }
        },
        {
          text: `Supprimer l'image`,
          handler: () => {
            this.confirmDeleteImage(imgFC)
          }
        },
        {
          text: `Annuler`,
          role: 'cancel',
        },
      ]
    }).then(actionSheet => {
      actionSheet.present();
    })
  }

  uploadImage(imgFC: FormControl<any>) {
    const options = Capacitor.getPlatform() === 'web'
    ? {
      source: CameraSource.Photos,
    }
    : {
      source: CameraSource.Prompt,
      promptLabelCancel: 'Annuler',
      promptLabelPicture: 'Prendre une photo',
      promptLabelPhoto: 'Depuis la gallerie',
    }

    Camera.getPhoto({
      resultType: CameraResultType.DataUrl,
      ...options
    }).then(res => {
      if (typeof res.dataUrl === 'string') {
        imgFC.setValue(res.dataUrl);
        imgFC.markAsDirty();
        imgFC.updateValueAndValidity();
        requestAnimationFrame(() => {
          this.resizedWindowTrigger$.next(true)
        });
      }
    })
  }


  async confirmDeleteImage(imgFC: FormControl<any>) {
    const alert = await this.alertController.create({
      message: 'Confirmez la suppression de la photo',
      buttons: [
        {
          text: 'Non',
          role: 'cancel',
          cssClass: 'secondary',
        }, {
          text: 'Oui',
          handler: () => {
            imgFC.setValue(null);
            imgFC.markAsDirty();
            imgFC.updateValueAndValidity();
            requestAnimationFrame(() => {
              this.resizedWindowTrigger$.next(true)
            });
          }
        }
      ]
    });
    await alert.present();
  }

  generateFormDynamique(field: App_FormSketchFormDynamique): FormGroup {
    return this.safeLiftService.generateFormDynamiqueSubGroupe(field, undefined, () => this.form, this.observeSourcesArrFromParent, this.contextFromParentPage);
  }

  editMarqueurInfo($event: { label: string, description: string, index: number }) {
    const marqueurs = this.markersFormControl?.value;
    if (Array.isArray(marqueurs)) {
      const found = marqueurs[$event.index];
      if (found) {
        const newMarqueursValue = [
          ...marqueurs.slice(0, $event.index),
          {
            ...marqueurs[$event.index],
            label: $event.label,
            description: $event.description,
          },
          ...marqueurs.slice($event.index + 1)
        ];
        this.markersFormControl?.setValue(newMarqueursValue);
      }
    }
  }

  private refreshDisplayBackgroundUrl(): void {
    this.displayBackgroundUrl = this.getBackgroundImageUrl();
  }

  /**
   * Priorité : valeur uploadée > image fixe (string, age=1) > image_default (mode upload).
   */
  private getBackgroundImageUrl(): string | null {
    const uploaded = this.imageUploadFormControl?.value;
    if (typeof uploaded === 'string' && uploaded.length > 0) {
      return uploaded;
    }
    const image = this.field?.fields?.image;
    if (typeof image === 'string' && image !== 'blank' && image.length > 0) {
      return image;
    }
    const imageDefault = this.field?.fields?.image_default;
    if (typeof imageDefault === 'string' && imageDefault.length > 0) {
      return imageDefault;
    }
    return null;
  }

  // 1: PREPARATION, 8: ELABORATION_A_CORRIGER ; 2: EN_COURS
  private readonly PREPARATION = new Set<number>([1, 8]);
  private readonly REMPLISSAGE = new Set<number>([2, 9]);

  isReadonly(): boolean {
    const rule = Number(this.field?.readonly ?? 0);
    const etat = this.etatId;
    
    switch (rule) {
      case 0: return false; // Non
      case 1: return true;  // Toujours
      case 2: return etat !== undefined && this.PREPARATION.has(etat); // En préparation
      case 3: return etat !== undefined && this.REMPLISSAGE.has(etat); // En exécution/Remplissage
      default: return false;
    }
  }

}
